import { useMemo } from "react";

import { useSelector } from "react-redux";

import { EMPTY_GROUPED_TECHNOLOGIES } from "app/pages/sales-intelligence/domain/technology/constants/model";
import useGroupedTechnologiesQuery from "app/pages/sales-intelligence/modules/technology/queries/useGroupedTechnologiesQuery";
import {
    selectIntentSignalCountriesDetails,
    selectIntentSignalSelectedTopics,
} from "app/pages/sales-intelligence/sub-modules/intent/store/signal/selectors/selectors";

import type SignalId from "../models/core/SignalId";
import type SignalsGroupedById from "../models/miscellaneous/SignalsGroupedById";
import type UISignalsGroupedById from "../models/view/UISignalsGroupedById";
import type UISignalsGroupedByType from "../models/view/UISignalsGroupedByType";
import signalsService from "../services/signalsService";
import {
    selectAllSignalsIdsFromSource,
    selectCountPerSignalId,
    selectDraftSignalsById,
    selectRawAllSignalsIdsFromSource,
    selectRawSignalsById,
    selectSignalsById,
    selectSignalsDescription,
} from "../store/selectors";

const useUISignalsGroupedByIdInternal = (
    signalIds: readonly string[],
    signalsById: Readonly<SignalsGroupedById>,
): Readonly<UISignalsGroupedById> => {
    const countPerSignalId = useSelector(selectCountPerSignalId);
    const signalsDescription = useSelector(selectSignalsDescription);
    const intentSelectedTopics = useSelector(selectIntentSignalSelectedTopics);
    const intentUserCountries = useSelector(selectIntentSignalCountriesDetails);

    const { data: groupedTechnologies = EMPTY_GROUPED_TECHNOLOGIES } = useGroupedTechnologiesQuery();

    return useMemo(
        () =>
            signalsService.createUISignalsGroupedById(
                signalIds,
                signalsById,
                countPerSignalId,
                signalsDescription,
                groupedTechnologies,
                intentSelectedTopics,
                intentUserCountries,
            ),
        [
            signalIds,
            signalsById,
            countPerSignalId,
            signalsDescription,
            groupedTechnologies,
            intentSelectedTopics,
            intentUserCountries,
        ],
    );
};

export const useUISignalsGroupedById = (): Readonly<UISignalsGroupedById> => {
    const signalIds = useSelector(selectAllSignalsIdsFromSource);
    const signalsById = useSelector(selectSignalsById);

    return useUISignalsGroupedByIdInternal(signalIds, signalsById);
};

export const useRawUISignalsGroupedById = (): Readonly<UISignalsGroupedById> => {
    const signalIds = useSelector(selectRawAllSignalsIdsFromSource);
    const signalsById = useSelector(selectRawSignalsById);

    return useUISignalsGroupedByIdInternal(signalIds, signalsById);
};

export const useAllRawUISignalsGroupedById = (): Readonly<UISignalsGroupedById> => {
    const signalsById = useSelector(selectRawSignalsById);
    const signalIds = useMemo(() => Object.keys(signalsById), [signalsById]);

    return useUISignalsGroupedByIdInternal(signalIds, signalsById);
};

const useUISignalsGroupedByTypeInternal = (
    uiSignalsGroupedById: Readonly<UISignalsGroupedById>,
): UISignalsGroupedByType =>
    useMemo(() => signalsService.groupUISignalsByType(Object.values(uiSignalsGroupedById)), [uiSignalsGroupedById]);

export const useUISignalsGroupedByType = (): UISignalsGroupedByType =>
    useUISignalsGroupedByTypeInternal(useUISignalsGroupedById());

export const useSignalNamesById = (): Record<SignalId, string> => {
    const uiSignalsGroupedByType = useUISignalsGroupedByType();

    return useMemo(
        () => signalsService.mapSignalsNamesTranslationToIds(uiSignalsGroupedByType),
        [uiSignalsGroupedByType],
    );
};

export const usePermanentUISignalsGroupedById = (): Readonly<UISignalsGroupedById> => {
    const uiSignalsGroupedById = useUISignalsGroupedById();
    const draftSignalsById = useSelector(selectDraftSignalsById);

    return useMemo(() => {
        if (Object.keys(draftSignalsById).length === 0) {
            return uiSignalsGroupedById;
        }

        return Object.fromEntries(
            Object.entries(uiSignalsGroupedById).filter(([id]) => !draftSignalsById[id]),
        ) as Readonly<UISignalsGroupedById>;
    }, [uiSignalsGroupedById, draftSignalsById]);
};

export const usePermanentUISignalsGroupedByType = (): UISignalsGroupedByType =>
    useUISignalsGroupedByTypeInternal(usePermanentUISignalsGroupedById());

export const usePermanentSignalsAmountBySource = (): number => {
    const allSignalIds = useSelector(selectAllSignalsIdsFromSource);
    const draftSignalsById = useSelector(selectDraftSignalsById);

    return allSignalIds.filter((id) => !draftSignalsById[id]).length;
};
